import CompanyTypePage from './CompanyTypePage';
import { useState, useEffect } from 'react';
import { companiesAPI } from '../services/api';
import { getView, setView, hasView } from '../services/viewCache';

const CONFIG = {
  icon: '📈',
  name: 'Public / Listed Company',
  shortName: 'Public / Listed',
  subtitle: 'Highest regulatory rigour — SEBI, MCA, Stock Exchange compliance · Companies Act 2013',
  tags: ['SEBI Compliant', 'Listed on BSE/NSE', 'Min 7 Shareholders', 'Min 3 Directors', 'Mandatory Audit Committee'],
  heroBg: 'linear-gradient(135deg, #f4f0e8, #e9e1d1)',
  heroAccent: '#c9b793',
  titleColor: '#5b4c35',
  subtitleColor: '#6f6049',
  tagBg: '#efe9e0',
  tagText: '#5b4c35',
  accentColor: '#6f6049',
  // Stats will be dynamically loaded
  stats: [],
  filings: [
    { form: 'AOC-4',    title: 'Financial Statements',      deadline: '30d from AGM',   penalty: '₹100/day',    status: 'filed' },
    { form: 'MGT-7',    title: 'Annual Return',              deadline: '60d from AGM',   penalty: '₹100/day',    status: 'filed' },
    { form: 'ADT-1',    title: 'Auditor Appointment',        deadline: '15d from AGM',   penalty: '1×–12× fee by delay',    status: 'filed' },
    { form: 'MR-1',     title: 'Managerial Remuneration',   deadline: '60d from AGM',   penalty: '₹500/day',    status: 'pending' },
    { form: 'LODR',     title: 'SEBI LODR Quarterly Report', deadline: 'Quarterly',      penalty: 'SEBI action', status: 'filed' },
  ],
  requirements: [
    { icon: '📈', title: 'SEBI LODR Compliance',  desc: 'SEBI (Listing Obligations & Disclosure Requirements) Reg 2015 — quarterly, half-yearly filings' },
    { icon: '👥', title: 'Board Composition',      desc: 'Min 3 directors · 1/3 Independent Directors · Woman Director mandatory if listed' },
    { icon: '🏛️', title: 'Committees',            desc: 'Mandatory: Audit, Nomination & Remuneration, Stakeholders Relationship, Risk Management' },
    { icon: '📋', title: 'Board Meetings',         desc: 'Minimum 4 per year, max 120 days gap · Video conferencing permissible' },
    { icon: '📊', title: 'Financial Reporting',    desc: 'Quarterly results within 45 days · Annual results within 60 days of FY end' },
    { icon: '🔒', title: 'Insider Trading',        desc: 'Strict SEBI PIT Regulations — trading window closure, disclosures, code of conduct' },
  ],
  complianceHealth: [
    { label: 'MCA Filings',       percent: 95, color: '#4a6b3f' },
    { label: 'SEBI LODR',        percent: 100, color: '#4a6b3f' },
    { label: 'Board Meetings',    percent: 100, color: '#4a6b3f' },
    { label: 'DIR-3 KYC',        percent: 100, color: '#4a6b3f' },
  ],
};

const CACHE_KEY = 'companyType:publicco';

export default function PublicCoPage() {
  // Stale-while-revalidate: seed the last real config so the stats strip does
  // not reset to empty on revisit (services/viewCache).
  const [dynamicConfig, setDynamicConfig] = useState(() => getView(CACHE_KEY) ?? CONFIG);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await companiesAPI.getPublicCoStats();
        if (response.success && response.data) {
          const stats = response.data;
          const dynamicStats = [
            { label: 'Listed Entities',    value: stats.totalCompanies.toString(), color: 'text-[#3a4a66]' },
            { label: 'Fully Compliant',    value: stats.fullyCompliant.toString(), color: 'text-[#4a6b3f]' },
            { label: 'SEBI Notices',       value: stats.overdueFilings.toString(), color: 'text-[#9a564a]' },
            { label: 'Pending Filings',    value: stats.pendingActions.toString(), color: 'text-[#8a6a1f]' },
          ];
          
          const nextConfig = { ...CONFIG, stats: dynamicStats };
          setDynamicConfig(nextConfig);
          setView(CACHE_KEY, nextConfig);
        }
      } catch (error) {
        console.error('Failed to fetch Public Co stats:', error);
        // Keep default stats if API fails
        const defaultStats = [
          { label: 'Listed Entities',    value: '0', color: 'text-[#3a4a66]' },
          { label: 'Fully Compliant',    value: '0', color: 'text-[#4a6b3f]' },
          { label: 'SEBI Notices',       value: '0', color: 'text-[#9a564a]' },
          { label: 'Pending Filings',    value: '0', color: 'text-[#8a6a1f]' },
        ];
        if (!hasView(CACHE_KEY)) {
          setDynamicConfig(prev => ({
            ...prev,
            stats: defaultStats
          }));
        }
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  if (loading) {
    return <CompanyTypePage config={dynamicConfig} />;
  }

  return <CompanyTypePage config={dynamicConfig} />;
}
